import { Box, DropdownMenu } from "@radix-ui/themes";
import WatsonApi from "../../../backends/WatsonApi";
import { useStores } from "../../../stores/useStores";
import SchoolBlocksLoadingIndicator from "../../utilities/SchoolBlocksLoadingIndicator";
import useSWRImmutable from "swr/immutable";

export type OrgFilter = Pick<BaseOrganization, "id" | "title" | "type">;

function DropdownGroup(props: {
  orgs: OrgFilter[];
  label: string;
  handleOrgClick: (org: OrgFilter) => void;
}) {
  return (
    <DropdownMenu.Group>
      <DropdownMenu.Label>{props.label}</DropdownMenu.Label>
      {props.orgs.map((org) => (
        <DropdownMenu.Item
          key={org.id}
          onClick={() => props.handleOrgClick(org)}
        >
          {org.title}
        </DropdownMenu.Item>
      ))}
    </DropdownMenu.Group>
  );
}

export function ListFilter(props: {
  setFilterOrganization: (org: OrgFilter | null) => void;
  filterOrganization: OrgFilter | null;
}) {
  const { organizationStore } = useStores();

  const { data: orgs, isLoading } = useSWRImmutable(
    "messaging_list_user_relevant_organizations",
    async () => {
      const client = await WatsonApi();
      const response = await client.apis.organizations.organizations_list({
        organization_pk: organizationStore.organization.id,
        path__descendant_or_eq: organizationStore.organization.path,
        with_role: true,
        deleted: false,
        type__in: ["school", "district", "classroom"],
        fields: ["id", "type", "title"],
      });
      const orgs = JSON.parse(response.data).data;
      return orgs.reduce(
        (acc, curr) => {
          if (curr.type === "school") {
            acc["schools"].push(curr);
          } else if (curr.type === "district") {
            acc["districts"].push(curr);
          } else if (curr.type === "classroom") {
            acc["classrooms"].push(curr);
          }
          return acc;
        },
        {
          classrooms: [],
          schools: [],
          districts: [],
        },
      );
    },
  );

  const handleOrgClick = async (org) => {
    if (org.id !== props.filterOrganization?.id) {
      props.setFilterOrganization(org);
    }
  };

  return (
    <>
      {isLoading ? (
        <Box style={{ textAlign: "center" }}>
          <SchoolBlocksLoadingIndicator />
        </Box>
      ) : (
        <>
          <DropdownMenu.Item onClick={() => props.setFilterOrganization(null)}>
            All Messages
          </DropdownMenu.Item>
          {orgs.classrooms.length > 0 && (
            <DropdownGroup
              orgs={orgs.classrooms}
              label={"Classrooms"}
              handleOrgClick={handleOrgClick}
            />
          )}
          {orgs.schools.length > 0 && (
            <DropdownGroup
              orgs={orgs.schools}
              label={"Schools"}
              handleOrgClick={handleOrgClick}
            />
          )}
          {orgs.districts.length > 0 && (
            <DropdownGroup
              orgs={orgs.districts}
              label={"Districts"}
              handleOrgClick={handleOrgClick}
            />
          )}
        </>
      )}
    </>
  );
}
